import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Mic, MicOff, AlertCircle } from 'lucide-react';
import { Translation } from '../types';
import { useChatContext } from '../context/ChatContext';
import { useVoice } from '../hooks/useVoice';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  translations: Translation;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onSend, isLoading, translations }) => {
  const { state } = useChatContext();
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isListening, transcript, setTranscript, startListening, stopListening, voiceError } =
    useVoice(state.selectedLanguage, true);

  useEffect(() => {
    if (transcript) {
      setInput((prev) => {
        const sep = prev.length > 0 && !prev.endsWith(' ') ? ' ' : '';
        return prev + sep + transcript;
      });
      setTranscript('');
    }
  }, [transcript, setTranscript]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (input.trim() && !isLoading) {
      onSend(input);
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const toggleMic = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isListening) stopListening();
    else startListening();
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  return (
    <div className="w-full max-w-4xl mx-auto relative">
      {voiceError && (
        <div className="absolute bottom-full left-0 mb-2 w-full flex justify-center">
          <div className="bg-red-900/80 backdrop-blur border border-red-800 text-white text-xs font-medium px-4 py-2 rounded-full flex items-center shadow-lg">
            <AlertCircle size={12} className="mr-2 text-red-400" />
            {voiceError}
          </div>
        </div>
      )}

      <div
        className={`bg-neutral-900 border transition-all duration-300 rounded-xl shadow-2xl p-2 md:p-3 flex items-end gap-2 ${
          isListening
            ? 'border-brand-500/50 shadow-[0_0_20px_rgba(45,212,191,0.08)]'
            : 'border-white/10 hover:border-white/20'
        }`}
      >
        <form onSubmit={handleSubmit} className="flex-1 flex items-end gap-2 w-full">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? 'Listening...' : translations.inputPlaceholder}
            disabled={isLoading}
            rows={1}
            className={`flex-1 bg-transparent text-gray-200 placeholder-gray-600 border-0 p-3 focus:ring-0 resize-none max-h-32 min-h-[44px] ${
              isListening ? 'placeholder-brand-400/50' : ''
            }`}
          />

          {/* Mic */}
          <button
            type="button"
            onClick={toggleMic}
            disabled={isLoading}
            className={`flex-shrink-0 p-3 rounded-lg transition-all duration-300 ${
              isListening
                ? 'bg-brand-900/30 text-brand-400 shadow-[0_0_15px_rgba(45,212,191,0.15)]'
                : 'text-gray-500 hover:text-brand-400 hover:bg-white/5'
            }`}
            title={isListening ? 'Stop Listening' : 'Start Voice Input'}
          >
            {isListening ? (
              <div className="relative">
                <MicOff className="w-5 h-5 relative z-10" />
                <span className="absolute inset-0 rounded-full bg-brand-500 opacity-20 animate-ping" />
              </div>
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>

          {/* Send */}
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className={`flex-shrink-0 p-3 rounded-lg transition-all duration-300 ${
              !input.trim() || isLoading
                ? 'bg-neutral-800 text-gray-600 cursor-not-allowed'
                : 'bg-brand-500 hover:bg-brand-400 text-neutral-950 shadow-lg shadow-brand-900/20'
            }`}
            aria-label={translations.sendButton}
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>
      </div>
    </div>
  );
};
